import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Row } from 'antd';
import PropTypes from 'prop-types';
import { isEmpty, isEqual, last, uniqWith } from 'lodash';
import {
  SectionContent,
  TitleContainer,
  CategoryLabel2,
  StyledSelect,
  ButtonContainer,
  StyledAddIcon,
  StyledCloseIcon
} from './styled';
import { PLACEHOLDER_SELECT } from '../../constants/cmConstants';
import { getCategoryLabel } from '../../common/utils';

const CombinationSelection = ({
  categoriesList,
  selectedCategories,
  onSave
}) => {
  const [selectedCombinations, setSelectedCombinations] = useState([[]]);
  const [isAllCombinationCovered, setIsAllCombinationCovered] = useState(false);
  const { category1, category2, category3 } = selectedCategories;

  useEffect(() => {
    setSelectedCombinations([[]]);
  }, [selectedCategories]);

  // common function for get category options from categorylist
  const getCategoryOptions = useCallback(
    (category) => {
      return (
        categoriesList.find(({ displayName }) => displayName === category)
          ?.values || []
      );
    },
    [categoriesList]
  );

  const prepareCombinations = (options1, options2, options3) => {
    return options1.reduce((result, option1) => {
      if (category2) {
        options2.forEach((option2) => {
          if (category3) {
            options3.forEach((option3) => {
              result.push([option1, option2, option3]);
            });
          } else {
            result.push([option1, option2]);
          }
        });
      } else {
        result.push([option1]);
      }

      return result;
    }, []);
  };

  const possibleCombinations = useMemo(() => {
    const options1 = getCategoryOptions(category1);
    const options2 = getCategoryOptions(category2);
    const options3 = getCategoryOptions(category3);

    return prepareCombinations(options1, options2, options3);
  }, [selectedCategories, getCategoryOptions]);

  const getValidCombinations = useCallback(
    (withLastSet) => {
      if (selectedCombinations.length > 0) {
        const previousCombinations = withLastSet
          ? selectedCombinations
          : selectedCombinations.slice(0, -1);
        const invalidCombinations = previousCombinations.reduce(
          (result, combination) => {
            let invalidOption1 = [];
            let invalidOption2 = [];
            let invalidOption3 = [];

            if (combination[0]?.value) {
              invalidOption1 =
                combination[0].value?.toUpperCase() === 'ALL'
                  ? getCategoryOptions(category1)
                  : ['All', combination[0].value];
            }

            if (combination[1]?.value) {
              invalidOption2 =
                combination[1].value?.toUpperCase() === 'ALL'
                  ? getCategoryOptions(category2)
                  : ['All', combination[1].value];
            }

            if (combination[2]?.value) {
              invalidOption3 =
                combination[2].value?.toUpperCase() === 'ALL'
                  ? getCategoryOptions(category3)
                  : ['All', combination[2].value];
            }

            const preparedCombinations = prepareCombinations(
              invalidOption1,
              invalidOption2,
              invalidOption3
            );
            if (preparedCombinations.length > 0) {
              result.push(...preparedCombinations);
            }

            return uniqWith(result, isEqual);
          },
          []
        );

        const validData = possibleCombinations.filter(
          (combination) =>
            !invalidCombinations.some((invalid) => {
              return (
                invalid[0]?.toUpperCase() === combination[0]?.toUpperCase() &&
                invalid[1]?.toUpperCase() === combination[1]?.toUpperCase() &&
                invalid[2]?.toUpperCase() === combination[2]?.toUpperCase()
              );
            })
        );

        return validData || [];
      } else {
        return possibleCombinations;
      }
    },
    [possibleCombinations, selectedCombinations]
  );

  const getCategory1Options = useCallback(
    (category) => {
      if (category) {
        let categoryOptions = getCategoryOptions(category); // All possible options
        // get previously selected combination based on the selected category1.
        const prevCombinations = selectedCombinations.slice(0, -1);

        if (prevCombinations.length > 0) {
          categoryOptions = categoryOptions.filter((option) =>
            getValidCombinations().some(
              (combination) =>
                combination[0]?.toUpperCase() === option?.toUpperCase()
            )
          );
        }

        return categoryOptions?.map((value) => ({
          label: value,
          value
        }));
      } else {
        return [];
      }
    },
    [selectedCombinations, getValidCombinations]
  );

  const getCategory2Options = useCallback(
    (category, selectedOption1) => {
      if (category && selectedOption1) {
        let categoryOptions = getCategoryOptions(category); // All possible options
        // get previously selected combination based on the selected category1.
        const prevCombinations = selectedCombinations.slice(0, -1);

        if (prevCombinations.length > 0) {
          const category2ValidCombinations = getValidCombinations().filter(
            (combination) =>
              combination[0]?.toUpperCase() === selectedOption1?.toUpperCase()
          );

          categoryOptions = categoryOptions.filter((option) =>
            category2ValidCombinations.some(
              (combination) =>
                combination[1]?.toUpperCase() === option?.toUpperCase()
            )
          );
        }

        return categoryOptions?.map((value) => ({
          label: value,
          value
        }));
      } else {
        return [];
      }
    },
    [selectedCombinations]
  );

  const getCategory3Options = useCallback(
    (category, selectedOption1, selectedOption2) => {
      if (category && selectedOption1 && selectedOption2) {
        let categoryOptions = getCategoryOptions(category); // All possible options
        // get previously selected combination based on the selected category1.
        const prevCombinations = selectedCombinations.slice(0, -1);

        if (prevCombinations.length > 0) {
          const category3ValidCombinations = getValidCombinations().filter(
            (combination) =>
              combination[0]?.toUpperCase() ===
                selectedOption1?.toUpperCase() &&
              combination[1]?.toUpperCase() === selectedOption2?.toUpperCase()
          );

          categoryOptions = categoryOptions.filter((option) =>
            category3ValidCombinations.some(
              (combination) =>
                combination[2]?.toUpperCase() === option?.toUpperCase()
            )
          );
        }

        return categoryOptions?.map((value) => ({
          label: value,
          value
        }));
      } else {
        return [];
      }
    },
    [selectedCombinations]
  );

  const onDropdownChange = (index, category, value, categoryIndex) => {
    let combinations = [...selectedCombinations];
    let items = combinations[index];

    if (categoryIndex == 0) {
      items = [{ category, value }];
    } else if (categoryIndex == 1) {
      items = [items[0], { category, value }];
    } else {
      items[categoryIndex] = { category, value };
    }

    combinations[index] = items;
    setSelectedCombinations(combinations);
  };

  const disbleAdd = useMemo(() => {
    if (isEmpty(selectedCategories)) {
      return true;
    } else if (selectedCombinations.length > 0) {
      const currentSets = last(selectedCombinations);
      const setsLength = currentSets?.filter(({ value }) => value)?.length;
      const categoriesLength = Object.values(selectedCategories)?.filter(
        (value) => value
      )?.length;

      if (setsLength === categoriesLength) {
        setIsAllCombinationCovered(getValidCombinations(true).length === 0);
      }

      // The combination should be equal to the categories selected.
      return setsLength !== categoriesLength;
    }

    return false;
  }, [selectedCategories, selectedCombinations]);

  const onAddClick = () => {
    if (!disbleAdd && !isAllCombinationCovered) {
      setSelectedCombinations((values) => [
        ...values,
        [{ category: category1 }]
      ]);
    }
  };

  const onDeleteCombination = (index) => {
    setSelectedCombinations((values) => values.filter((_, i) => i !== index));
  };

  return (
    <>
      <TitleContainer>
        <div className="section-no">2</div>
        <div>Select Details</div>
      </TitleContainer>
      <SectionContent>
        <div className="selection-info">
          You are selecting details for the following categories
        </div>
        <Row gutter={[32, 32]}>
          <Col span={8}>
            <CategoryLabel2>Category 01</CategoryLabel2>
            <div>{getCategoryLabel(category1) || '--'}</div>
          </Col>
          <Col span={8}>
            <CategoryLabel2>Category 02</CategoryLabel2>
            <div>{getCategoryLabel(category2) || '--'}</div>
          </Col>
          <Col span={8}>
            <CategoryLabel2>Category 03</CategoryLabel2>
            <div>{getCategoryLabel(category3) || '--'}</div>
          </Col>
        </Row>
        <br />
        <br />
        <Row gutter={[32, 16]} align="middle">
          {selectedCombinations.map((combination, index) => (
            <React.Fragment key={index}>
              <Col span={8}>
                <StyledSelect
                  disabled={
                    !category1 || index + 1 !== selectedCombinations.length
                  }
                  options={getCategory1Options(category1)}
                  onChange={(value) =>
                    onDropdownChange(index, category1, value, 0)
                  }
                  placeholder={PLACEHOLDER_SELECT}
                  value={combination[0]?.value}
                />
              </Col>
              <Col span={8}>
                <StyledSelect
                  disabled={
                    !category2 ||
                    index + 1 !== selectedCombinations.length ||
                    !combination[0]?.value
                  }
                  options={getCategory2Options(
                    category2,
                    combination[0]?.value
                  )}
                  onChange={(value) =>
                    onDropdownChange(index, category2, value, 1)
                  }
                  placeholder={PLACEHOLDER_SELECT}
                  value={combination[1]?.value}
                />
              </Col>
              <Col span={8}>
                <StyledSelect
                  disabled={
                    !category3 ||
                    index + 1 !== selectedCombinations.length ||
                    !combination[1]?.value
                  }
                  options={getCategory3Options(
                    category3,
                    combination[0]?.value,
                    combination[1]?.value
                  )}
                  onChange={(value) =>
                    onDropdownChange(index, category3, value, 2)
                  }
                  placeholder={PLACEHOLDER_SELECT}
                  value={combination[2]?.value}
                />
                {index + 1 === selectedCombinations.length && (
                  <StyledAddIcon
                    onClick={onAddClick}
                    disabled={disbleAdd || isAllCombinationCovered}
                    index={index}
                  />
                )}
                {selectedCombinations.length > 1 && (
                  <StyledCloseIcon onClick={() => onDeleteCombination(index)} />
                )}
              </Col>
            </React.Fragment>
          ))}
        </Row>
        <ButtonContainer className="segment-button-color">
          <Button
            type="primary"
            disabled={selectedCombinations.length === 0 || disbleAdd}
            onClick={() => onSave(selectedCombinations)}>
            Save Configuration
          </Button>
        </ButtonContainer>
      </SectionContent>
    </>
  );
};

CombinationSelection.propTypes = {
  categoriesList: PropTypes.array.isRequired,
  selectedCategories: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired
};

export default CombinationSelection;
